/**
 * transform-hero block
 *
 * Event header for C1 Transform. A large display title with a two-line
 * "lockup" stacked to its right (e.g. CONFERENCE / 2026), an optional
 * subheading and CTA — inspired by the GitHub Universe title lockup.
 *
 * The title is rendered as an inline SVG wordmark so it can occupy an exact
 * frame (default 826.66 × 141.78 on desktop) and scale down proportionally
 * on smaller screens. `textLength` forces the width to the frame regardless
 * of font metrics; the viewBox locks the aspect ratio. A visually-hidden
 * real <h1> text is kept for SEO / accessibility.
 *
 * The block has two header shapes.
 *
 * The campaign's own landing page runs eyebrow -> lockup: the wordmark filling
 * the width with the big year beside it, inside the <h1>.
 *
 * A sub-page (`page_title` set) instead sets the wordmark and the page's own
 * name on ONE line — "Transform Agenda" — with the eyebrow beneath. It carries
 * neither the year nor the CONFERENCE/2026 side lockup: the line is a sentence,
 * and a badge in the middle of it would be read as punctuation. The wordmark is
 * sized off the page title so the two always match (see `WORDMARK_INK`).
 *
 * `announcement` puts a dark teal (`teal-dark`, the brand's primary surface)
 * strip with white text above either header shape, for a short campaign-level
 * message such as a post-event thank-you.
 */
import { Button } from "~/components/Button";

/**
 * Cap height of the display face (Anybody SemiBold), as a fraction of its font
 * size. Measured off the rendered font rather than assumed: it is what the
 * wordmark beside a title has to match for the two to look the same size.
 */
const DISPLAY_CAP_RATIO = 0.68;

/**
 * How many times the page title's cap height the wordmark's cap height runs, on
 * a sub-page title line. At 1 the two match exactly and the mark reads as
 * another word in the phrase; above 1 the graphic leads and the page name
 * follows it, which is the intent.
 *
 * It multiplies the cap-matched height, so it is still one derived value — the
 * wordmark cannot drift away from the title across breakpoints, and the
 * baseline nudge re-derives from the new height rather than being left behind
 * at the old one.
 *
 * 1.25 is set by the narrowest width the pair has to share a line: a 1024px
 * viewport, where the title is already at its 76px `md` size and the column is
 * 945px. The pair measures 792px there — 84% of the column. The geometry would
 * allow ~1.64 before it touched the edge; this stops well short of crowding.
 */
const WORDMARK_CAP_MULTIPLE = 1.25;

/**
 * Where the ink actually sits inside each wordmark source, as fractions of that
 * source's own box.
 *
 * A box is not a letter. The GIF carries five transparent pixels of padding on
 * every side and its "f" overshoots the capital "T", so matching box heights
 * would set the wordmark visibly small and floating above the baseline. These
 * are measured from the asset itself — the cap ("T") spans rows 9..100 of 108,
 * and row 100 is the baseline the "m" and "n" sit on — and they are what makes
 * the optical match hold: cap height to cap height, baseline to baseline.
 *
 * `cap` is the cap height as a fraction of the box; `baselineFromBottom` is how
 * far the baseline sits above the box's bottom edge, same units.
 */
const WORDMARK_INK = {
  /** 688 × 108 GIF: cap rows 9..100, baseline row 100. */
  image: { cap: 91 / 108, baselineFromBottom: 8 / 108, aspect: 688 / 108 },
  /**
   * The inline SVG fallback, whose text is drawn at `fontSize = frameHeight`
   * with `dominant-baseline: central` — so its cap is the face's own
   * `DISPLAY_CAP_RATIO` and its baseline lands half an x-height below the
   * frame's middle.
   */
  svg: { cap: DISPLAY_CAP_RATIO, baselineFromBottom: 0.205, aspect: 826.66 / 141.78 },
} as const;

export function TransformHero({
  title = "Transform",
  title_image,
  year_short,
  lockup_top = "CONFERENCE",
  lockup_bottom = "2026",
  eyebrow,
  subheading,
  page_title,
  sr_heading,
  button_primary,
  image,
  image_alt,
  title_frame_width = 826.66,
  title_frame_height = 141.78,
  announcement,
}: {
  title?: string;
  /** Optional wordmark image/GIF that replaces the SVG title. Kept at its
   *  native aspect ratio, capped to the title frame width, scaling down on
   *  smaller screens. `title` still supplies the accessible heading text. */
  title_image?: string;
  /** Large short year (e.g. "26") shown to the right of the wordmark in
   *  Anybody Expanded SemiBold, sized to visually match the wordmark height.
   *  When set, the wordmark grows to fill the remaining width and the side
   *  lockup is not rendered. */
  year_short?: string;
  /** Small tracked line above the year in the side lockup. */
  lockup_top?: string;
  /** Large accent line (the year) in the side lockup. */
  lockup_bottom?: string;
  /** Optional label above the title lockup. */
  eyebrow?: string;
  /** Dates / location line under the title. Supports <br/>. */
  subheading?: string;
  /**
   * The name of THIS page, set in the display face at the top of the header —
   * for a page inside the campaign (an agenda, a schedule) that has to carry
   * the same header as the campaign's own landing page while still announcing
   * what it is. Setting it also shrinks the lockup below (see
   * `SUBPAGE_LOCKUP_SCALE`). It is `aria-hidden` because `sr_heading` already
   * spells the whole thing out; without that the heading would read its own
   * last word twice.
   */
  page_title?: string;
  /**
   * Overrides the visually-hidden text of the `<h1>`. The derived default
   * concatenates the wordmark, the year and the lockup lines, which reads as
   * "Transform 26" — right for the landing page, wrong for a sub-page whose
   * heading should name the page.
   */
  sr_heading?: string;
  button_primary?: { text: string; url: string; target?: string; rel?: string };
  /** Optional illustration below the title. */
  image?: string;
  image_alt?: string;
  /** Desktop frame the title wordmark fills, in px. Scales down proportionally. */
  title_frame_width?: number;
  title_frame_height?: number;
  /** Optional one-line message in a pink strip above the title. */
  announcement?: string;
}) {
  const srHeading =
    sr_heading ||
    [title, year_short, lockup_top, lockup_bottom].filter(Boolean).join(" ");
  // Fluid lockup size: caps at 29.7px on desktop (~1280px viewport) and
  // scales down with the viewport so the lockup shrinks in step with the
  // GIF and both stay on one line across screen sizes.
  const lockupFontSize = "clamp(13px, 2.5vw, 32px)";
  // Big year (e.g. "26"): fluid so it scales with the wordmark; tuned so its
  // glyph height ≈ the filled wordmark height and the row spans full width.
  const yearFontSize = "min(149px, 11.6vw)";

  // The sub-page's one-line title. Everything is expressed in `em` against the
  // page title's own font size, so there is a single source of truth for how
  // big this header is — change the title size and the wordmark follows,
  // through every breakpoint, with no second number to keep in step.
  const ink = title_image ? WORDMARK_INK.image : WORDMARK_INK.svg;
  // Height that puts the wordmark's CAP at `WORDMARK_CAP_MULTIPLE` times the
  // title's cap — not its box at some multiple of the title's box.
  const wordmarkHeightEm =
    (DISPLAY_CAP_RATIO * WORDMARK_CAP_MULTIPLE) / ink.cap;
  // ...and the drop that puts its baseline on the text's. A flex baseline
  // aligns an image by its bottom edge, which is below the ink's baseline by
  // this much, so the image is nudged down by exactly that. Derived FROM the
  // height above, so a taller wordmark grows upward off the shared baseline
  // instead of floating off it.
  const wordmarkDropEm = ink.baselineFromBottom * wordmarkHeightEm;

  /* The lockup row itself. Extracted rather than inlined twice because the two
     header shapes differ only in where it sits and whether it is the <h1>. */
  const lockup = (
    <span
      aria-hidden="true"
      className="flex flex-row items-end gap-4 sm:gap-8"
    >
      {title_image ? (
        <span
          className={`block min-w-0 ${year_short ? "flex-1" : "w-full"}`}
          /* `flex-1` lets the wordmark take the width the year leaves. */
          style={year_short ? undefined : { maxWidth: `${title_frame_width}px` }}
        >
          <img
            src={title_image}
            alt=""
            className="block h-auto w-full"
          />
        </span>
      ) : (
        title && (
          <span className="block w-full min-w-0" style={{ maxWidth: `${title_frame_width}px` }}>
            <svg
              viewBox={`0 0 ${title_frame_width} ${title_frame_height}`}
              className="block h-auto w-full"
              role="img"
              aria-hidden="true"
            >
              <text
                x="0"
                y={title_frame_height / 2}
                textLength={title_frame_width}
                lengthAdjust="spacingAndGlyphs"
                dominantBaseline="central"
                fontFamily="Anybody, Inter, sans-serif"
                fontWeight={700}
                fontSize={title_frame_height}
                fill="currentColor"
              >
                {title}
              </text>
            </svg>
          </span>
        )
      )}
      {year_short && (
        <span
          className="shrink-0 self-end font-semibold leading-none text-charcoal"
          style={{
            fontFamily: '"Anybody Expanded", sans-serif',
            fontSize: yearFontSize,
            letterSpacing: "-0.01em",
            transform: "translateY(0.173em)",
          }}
        >
          {year_short}
        </span>
      )}
      {!year_short && (lockup_top || lockup_bottom) && (
        <span className="shrink-0 flex flex-col items-start leading-none">
          {lockup_top && (
            <span
              className="inline-block bg-[#231F20] text-white uppercase leading-none font-normal tracking-normal"
              style={{ fontFamily: '"Anybody Expanded", sans-serif', fontSize: lockupFontSize }}
            >
              {lockup_top}
            </span>
          )}
          {lockup_bottom && (
            <span
              className="inline-block bg-[#FF92EB] text-[#231F20] uppercase leading-none font-semibold tracking-normal mt-1"
              style={{ fontFamily: '"Anybody Expanded", sans-serif', fontSize: lockupFontSize }}
            >
              {lockup_bottom}
            </span>
          )}
        </span>
      )}
    </span>
  );

  /* The sub-page's title line: the wordmark, then the page's own name, on one
     line. `items-baseline` is what lines them up — the wordmark's own baseline
     is put on the text's by `wordmarkDropEm`, so the pair reads as one setting
     of one phrase rather than as an image parked next to a word.

     It wraps rather than shrinking: at phone width the two do not fit on a line
     (see the note on the header shape below), and a wrapped wordmark over its
     title still reads as the same phrase, where a pair scaled down to fit reads
     as a smaller header. */
  const titleLine = page_title && (
    <span
      aria-hidden="true"
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-display text-[40px] font-semibold leading-[1.05] tracking-[-0.02em] md:gap-x-5 md:text-[76px]"
    >
      {title_image ? (
        <img
          src={title_image}
          alt=""
          className="block w-auto max-w-full shrink-0"
          style={{
            height: `${wordmarkHeightEm}em`,
            transform: `translateY(${wordmarkDropEm}em)`,
          }}
        />
      ) : (
        title && (
          <svg
            viewBox={`0 0 ${title_frame_width} ${title_frame_height}`}
            className="block w-auto max-w-full shrink-0"
            role="img"
            aria-hidden="true"
            style={{
              height: `${wordmarkHeightEm}em`,
              transform: `translateY(${wordmarkDropEm}em)`,
            }}
          >
            <text
              x="0"
              y={title_frame_height / 2}
              textLength={title_frame_width}
              lengthAdjust="spacingAndGlyphs"
              dominantBaseline="central"
              fontFamily="Anybody, Inter, sans-serif"
              fontWeight={700}
              fontSize={title_frame_height}
              fill="currentColor"
            >
              {title}
            </text>
          </svg>
        )
      )}
      <span className="block">{page_title}</span>
    </span>
  );

  return (
    <div
      className={`px-4 md:px-8 pt-[88px] ${
        /* A sub-page header takes the site header's own `bg-cream` so the two
           read as one surface down to the agenda's edge — the navbar is cream
           on this route, so white here drew a seam across the top of the page.
           The campaign's landing page keeps `bg-white`, which its own hero was
           designed against.

           It also ends on its own title rather than on a subheading or a CTA,
           so it carries its own bottom margin; without that the surface is cut
           flush against the title's descenders and reads as a crop. */
        page_title ? "bg-cream pb-10 md:pb-14" : "bg-white"
      }`}
    >
      <div className="max-w-screen-xl mx-auto">
        {announcement && (
          /* Above the title rather than below it: on a past event this is the
             first thing a visitor needs to read, before the lockup. */
          <p className="mb-10 bg-teal-dark px-5 py-4 text-center font-display text-lg font-semibold leading-snug text-white md:mb-14 md:px-8 md:py-5 md:text-2xl">
            {announcement}
          </p>
        )}
        {/* Title lockup — left-aligned header copy on the page background. */}
        {page_title ? (
          <>
            {/* The title line is `aria-hidden` (inside `titleLine`) because
                `srHeading` already spells the whole page out; without that the
                heading would read its own last word twice. */}
            <h1 className="text-charcoal">
              {srHeading && <span className="sr-only">{srHeading}</span>}
              {titleLine}
            </h1>
            {eyebrow && (
              /* Below the title now, and last: the header is two elements, so
                 the gap only has to say "these belong together" — hence one
                 modest step rather than the two different gaps three stacked
                 elements needed. The eyebrow cannot live in the <h1> — it is a
                 sibling paragraph, so nesting it would be invalid. */
              <p className="font-body text-charcoal/70 font-semibold uppercase tracking-widest-2 text-sm mt-5 md:mt-6">
                {eyebrow}
              </p>
            )}
          </>
        ) : (
          <>
            {eyebrow && (
              <p className="font-body text-charcoal/70 font-semibold uppercase tracking-widest-2 text-sm mb-4">
                {eyebrow}
              </p>
            )}
            <h1 className="text-charcoal">
              {srHeading && <span className="sr-only">{srHeading}</span>}
              {lockup}
            </h1>
          </>
        )}

        {subheading && (
          <p
            className="font-body text-charcoal/80 text-lg md:text-xl leading-relaxed mt-6 max-w-2xl"
            dangerouslySetInnerHTML={{ __html: subheading }}
          />
        )}

        {button_primary && (
          <div className="mt-8">
            <Button
              href={button_primary.url}
              variant="teal"
              size="xl"
              target={button_primary.target}
              rel={button_primary.rel}
            >
              <span>{button_primary.text}</span>
            </Button>
          </div>
        )}

        {image && (
          <div className="mt-12 lg:mt-16">
            <img
              src={image}
              alt={image_alt || ""}
              className="w-full block"
              loading="lazy"
            />
          </div>
        )}
      </div>
    </div>
  );
}
